"use client";

import * as React from "react";
import { Flex } from "@radix-ui/themes";
import { type ReactNode } from "react";
import { AlertDialog, Button } from "./elements.js";
import { useDeleteTotpFactors } from "../api/endpoint.js";
import { useSecuritySettings } from "./use-security-settings.js";
import { ElevatedAccess } from "./elevated-access.js";
import { SaveButton } from "./save-button.js";
import { useDialogClose } from "./use-dialog-close.js";
import { Translation } from "./i18n/translation.js";

interface ResetMfaDialogProps extends AlertDialog.RootProps {
  children?: ReactNode;
  isPasswordSet: boolean;
}

export function ResetMfaDialog({
  children,
  isPasswordSet,
  ...props
}: ResetMfaDialogProps) {
  const [open, setOpen] = React.useState(false);

  const handleClose = React.useCallback(() => {
    setOpen(false);
  }, []);

  return (
    <AlertDialog.Root {...props} open={open} onOpenChange={setOpen}>
      <AlertDialog.Trigger>{children}</AlertDialog.Trigger>

      <AlertDialog.Content maxWidth="480px">
        <ElevatedAccess type="alert">
          <Content onClose={handleClose} isPasswordSet={isPasswordSet} />
        </ElevatedAccess>
      </AlertDialog.Content>
    </AlertDialog.Root>
  );
}

function Content({
  onClose,
  isPasswordSet,
}: {
  onClose: () => void;
  isPasswordSet: boolean;
}) {
  const securitySettings = useSecuritySettings();
  const resetMfa = useDeleteTotpFactors();

  const onSubmitForm = () => {
    resetMfa.mutate();
  };

  useDialogClose(resetMfa.isSuccess, () => {
    securitySettings.update("Mfa", false);
  });

  return (
    <>
      <AlertDialog.Title>
        <Translation
          defaultMessage="Disable multi-factor authentication?"
          id="a4wDlZ"
          description="Dialog title asking to confirm disabling MFA"
        />
      </AlertDialog.Title>
      <AlertDialog.Description>
        <Translation
          defaultMessage="Turning off MFA will remove the additional layer of security on your account."
          id="24huc/"
          description="Warning that disabling MFA reduces security"
          trailingSpace
        />
        {isPasswordSet ? (
          <Translation
            defaultMessage="We will only ask for your password during sign-in."
            id="Z6coCJ"
            description="Explanation when password is set but MFA will be disabled"
          />
        ) : (
          <Translation
            defaultMessage="We will not ask for additional verification during sign-in."
            id="4SH+qg"
            description="Explanation when no password set and MFA will be disabled"
          />
        )}
      </AlertDialog.Description>

      <Flex gap="3" justify="end" mt="5" asChild>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            onSubmitForm();
          }}
        >
          <AlertDialog.Cancel>
            <Button
              variant="secondary"
              disabled={resetMfa.isPending || resetMfa.isSuccess}
            >
              <Translation
                defaultMessage="Cancel"
                id="OdCEs1"
                description="Button to cancel disabling MFA"
              />
            </Button>
          </AlertDialog.Cancel>

          <SaveButton
            type="submit"
            loading={resetMfa.isPending}
            done={resetMfa.isSuccess}
            onDone={onClose}
            variant="destructive"
          >
            <Translation
              defaultMessage="Disable"
              id="uvxVJM"
              description="Button to confirm disabling MFA"
            />
          </SaveButton>
        </form>
      </Flex>
    </>
  );
}
